import { text } from '../data';
import React, {useState, useEffect, useContext, forwardRef, useImperativeHandle, useReducer, ChangeEvent} from 'react';
import { useNavigate } from 'react-router-dom';
import { AnimationDispatchContext } from '../contexts/PageContext';
import {
	trackGuidedHelpOffRamp,
	trackGuidedHelpPath,
} from '../../analytics/engineering-tools';
import { OptionItem } from '../types/types';

interface NavigationBarProps {
	prev?: string;
	next?: string;
	first?: boolean;
	last?: boolean;
	redirect?: OptionItem;
}

interface NextPageHandle {
	handleNextPage: (value: string) => void;
}

const NavigationBar = forwardRef<NextPageHandle, NavigationBarProps>(({ prev, next, first, last, redirect }, ref) => {
	const [ nav, setNav ] = useState<string | undefined>( undefined );
	const dispatch = useContext( AnimationDispatchContext );
	const navigate = useNavigate();

	useEffect( () => {
		if ( nav ) {
			navigate( `/${ nav }` );
		}
	}, [ nav ] );

	const handleNextPage = (value: string) => {
		setNav(value);
	}

	useImperativeHandle(ref, () => ({
		handleNextPage,
	}));

	const toPage = ( page?: string ) => {
		if ( page ) {
			const direction = page === 'start' ? -1 : 1;

			if ( dispatch ) {
				dispatch( {
					type: 'directionChanged',
					direction,
				} );

				setNav( page );
			}
		}
	};

	const goBack = () => {
		if ( dispatch ) {
			dispatch( {
				type: 'directionChanged',
				direction: -1,
			} );
		}

		setNav( prev );
	};

	const trackOffRampPath = ( link: OptionItem ) => {
		trackGuidedHelpPath( link.id );
		trackGuidedHelpOffRamp( link.label );
	};

	let nextElement: React.ReactNode;

	if ( redirect ) {
		nextElement = (
			<a
				href={next}
				className="btn btn--primary"
				onClick={() => trackOffRampPath( redirect )}
			>
				<span>{text.next}</span>
				<span className="btn__icon icon-caret-right" aria-hidden="true"></span>
			</a>
		);
	} else if ( last ) {
		nextElement = (
			<button className="btn btn--secondary" onClick={() => toPage( 'start' )}>
				{text.startOver}
			</button>
		);
	} else {
		nextElement = (
			<button
				className="btn btn--primary"
				onClick={() => toPage( next )}
				disabled={! next}
			>
				<span>{text.next}</span>
				<span className="btn__icon icon-caret-right" aria-hidden="true"></span>
			</button>
		);
	}

	return (
		<AnimationDispatchContext.Provider value={dispatch}>
			<div className="c-guided-help__navigation">
				<button
					className="btn btn--secondary btn--icon-only"
					onClick={goBack}
					disabled={first}
					style={{ opacity: first ? 0 : 1 }}
				>
					<span className="sr-only">{text.back}</span>
					<span className="btn__icon icon-caret-left" aria-hidden="true"></span>
				</button>

				{nextElement}
			</div>
		</AnimationDispatchContext.Provider>
	);
});

export default NavigationBar;
